import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import {
  required,
  useTranslate,
  FileInput as RaFileInput,
  FileField as RaFileField,
} from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';
import { FileInputProps, UploadedFile } from './types';

type AttachmentResponse = {
  id: string | number;
  upload_url: string;
  read_url?: string;
};

const FileInput: FunctionComponent<FileInputProps> = (props) => {
  const { source, resource, label, record } = props;
  const t = useTranslate();
  const form = useForm();
  const formState = useFormState();
  const config = useConfig();

  const isExpertInvoice = record?.user_kind === 'expert';
  const isClientInvoice = record?.user_kind === 'client';
  const isSingleUpload = isExpertInvoice || props?.options?.multiple === false;
  const accept =
    props?.options?.accept ||
    (isExpertInvoice ? '.pdf' : '.doc,.docx,.pdf,.ptt,.pptx,.xls,.xlsx');
  const kind = isExpertInvoice
    ? 'invoice'
    : props?.options?.kind || 'attachment';
  const maxFileSize = 2 * 1024 * 1024; // 2 MB

  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<
    UploadedFile[]
  >([]);
  const uploadedAttachmentsRef = useRef<UploadedFile[]>([]);
  const formAttachments = formState.values[source];

  const validateFileSize = (value: any) => {
    if (!value) return undefined;
    const files = Array.isArray(value) ? value : [value];
    const tooBig = files.find((file) => file.rawFile?.size > maxFileSize);

    return tooBig
      ? t('mission.modal.apply.proforma.attachment.infoText')
      : undefined;
  };

  const validateFileRequired = (value: any) => {
    if (!value) {
      if (!record) return undefined;

      const isProforma = record.status === 'proforma';
      const isCreation = !record.id;
      const isAdminCreated = record.isAdminCreated === true;

      if (isProforma && !isCreation && !isAdminCreated) {
        return undefined;
      }

      return t('fields.validators.required');
    }

    return undefined;
  };

  const handlePostAttachment = (file: any) =>
    httpClient(`${config.apiUrl}/attachments`, {
      method: 'POST',
      body: JSON.stringify({
        read_url: file.src,
        name: file.title || file.rawFile?.name,
        mime_type: file.rawFile?.type,
        size: file.rawFile?.size,
        kind,
        file_uuid: uuidv4(),
      }),
    });

  const handlePutAttachment = async (
    attachment: { upload_url: string },
    file: any
  ) =>
    axios.put(attachment.upload_url, file.rawFile, {
      headers: { 'Content-Type': file.rawFile.type },
    });

  const uploadAttachment = async (
    formAttachment: any
  ): Promise<UploadedFile | null> => {
    if (!formAttachment?.rawFile) return null;

    try {
      if (isSingleUpload && uploadedAttachmentsRef.current.length === 1) {
        const oldFile = uploadedAttachmentsRef.current[0];
        if (oldFile?.id) {
          await httpClient(`${config.apiUrl}/attachments/${oldFile.id}`, {
            method: 'DELETE',
          });
        }
      }

      const { json } = await handlePostAttachment(formAttachment);
      const { id, upload_url, read_url } = json as AttachmentResponse;

      await handlePutAttachment({ upload_url }, formAttachment);

      return {
        id: String(id),
        src: read_url || formAttachment.src || '',
        title: (
          formAttachment.title ||
          formAttachment.rawFile.name ||
          ''
        ).replace(/\.pdf\.pdf$/, '.pdf'),
        rawFile: formAttachment.rawFile,
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!record || !resource) return;

    const rawExistingFiles =
      isExpertInvoice && record.pdfs ? record.pdfs : record[source];
    const existingFiles = Array.isArray(rawExistingFiles)
      ? rawExistingFiles
      : rawExistingFiles
      ? [rawExistingFiles]
      : [];

    if (
      uploadedAttachmentsRef.current.length === 0 &&
      existingFiles.length > 0
    ) {
      const initial: UploadedFile[] = existingFiles.map((file: any) => ({
        id: String(file.id),
        src: file.public_url || file.src || '',
        title: file.name || file.title || '',
      }));

      uploadedAttachmentsRef.current = initial;
      setUploadedAttachments(initial);
      setAttachmentIds(initial.map((file) => file.id));
      form.change(source, isSingleUpload ? initial[0] : initial);

      return;
    }

    if (!formAttachments) return;

    const attachmentArray = isSingleUpload
      ? formAttachments
        ? [formAttachments]
        : []
      : Array.isArray(formAttachments)
      ? formAttachments
      : [];

    const toUpload = attachmentArray.filter(
      (file) =>
        file?.rawFile &&
        !uploadedAttachmentsRef.current.some((f) => f.src === file.src)
    );

    const filesToUpload = isSingleUpload ? toUpload.slice(0, 1) : toUpload;

    if (filesToUpload.length > 0) {
      Promise.all(filesToUpload.map(uploadAttachment)).then((results) => {
        const validUploads = results.filter(
          (r): r is UploadedFile => r !== null
        );
        if (!validUploads.length) return;

        const updatedUploads = isSingleUpload
          ? validUploads
          : [...uploadedAttachmentsRef.current, ...validUploads];

        const currentSrcs = attachmentArray.map((file) => file.src);
        const filtered = updatedUploads.filter((file) =>
          currentSrcs.includes(file.src)
        );
        uploadedAttachmentsRef.current = filtered;
        setUploadedAttachments(filtered);
        setAttachmentIds(filtered.map((file) => file.id));
        form.change(source, isSingleUpload ? filtered[0] : filtered);
      });
    } else {
      const currentSrcs = attachmentArray.map((file) => file.src);
      const remaining = uploadedAttachmentsRef.current.filter((file) =>
        currentSrcs.includes(file.src)
      );

      const hasChanged =
        remaining.length !== uploadedAttachmentsRef.current.length ||
        remaining.some(
          (f, i) => f.src !== uploadedAttachmentsRef.current[i]?.src
        );

      if (hasChanged) {
        uploadedAttachmentsRef.current = remaining;
        setUploadedAttachments(remaining);
        setAttachmentIds(remaining.map((file) => file.id));

        if (remaining.length > 0) {
          form.change(source, isSingleUpload ? remaining[0] : remaining);
        } else {
          form.change(source, isSingleUpload ? null : []);
        }
      }
    }
  }, [
    record,
    resource,
    source,
    form,
    formAttachments,
    isSingleUpload,
    isExpertInvoice,
  ]);

  // Update hidden field for attachment ids
  useEffect(() => {
    form.change(`${resource}_${source}`, attachmentIds);
  }, [attachmentIds, form, resource, source]);

  // Override form.submit to delete removed files on server when submitting
  useEffect(() => {
    const originalSubmit = form.submit;

    form.submit = async () => {
      const current = formState.values[source];
      const currentArray = isSingleUpload
        ? current
          ? [current]
          : []
        : Array.isArray(current)
        ? current
        : [];

      const currentSrcs = currentArray.map((file) => file?.src).filter(Boolean);

      const removedFiles = uploadedAttachmentsRef.current.filter(
        (file) => !currentSrcs.includes(file.src)
      );

      await Promise.all(
        removedFiles.map(async (file) => {
          if (!file.id) return;

          try {
            await httpClient(`${config.apiUrl}/attachments/${file.id}`, {
              method: 'DELETE',
            });
          } catch (error: any) {
            if (error.status !== 404) {
              throw error;
            }
          }
        })
      );

      const keptFiles = uploadedAttachmentsRef.current.filter((file) =>
        currentSrcs.includes(file.src)
      );

      uploadedAttachmentsRef.current = keptFiles;
      setUploadedAttachments(keptFiles);
      setAttachmentIds(keptFiles.map((f) => f.id));

      if (isSingleUpload) {
        form.change(source, keptFiles.length ? keptFiles[0] : null);
      } else {
        form.change(source, keptFiles.length ? keptFiles : []);
      }

      return originalSubmit();
    };

    return () => {
      form.submit = originalSubmit;
    };
  }, [form, source, formState.values, isSingleUpload]);

  return (
    <>
      {!isClientInvoice && (
        <RaFileInput
          source={source}
          label={label}
          accept={accept}
          multiple={!isSingleUpload}
          validate={[
            validateFileSize,
            ...(isExpertInvoice ? [validateFileRequired] : []),
          ]}
        >
          <RaFileField source="src" title="title" />
        </RaFileInput>
      )}
    </>
  );
};

export default FileInput;
